import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { GlassSurface } from "./GlassSurface";

/**
 * AppShell - the experience-OS frame (rail / center / Brain region).
 *
 * The single structural host for the Creator-Studio surfaces. Delta 004 model:
 * a fixed, full-viewport box with an ambient FIELD layer behind everything,
 * then TWO PLANES laid out as a FLEX ROW:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │ field (absolute, pointer-events:none, z-0)            │
 *   │ ┌──────╷──────────────────────────┐  ╔════════════╗   │
 *   │ │ rail │ center (flex-1 min-w-0)   │  ║ Brain      ║   │
 *   │ │ 0 0  │ {children - routed view}  │  ║ GlassSurf. ║   │  ← flush base + floating glass
 *   │ │ rail │                           │ ◂║ splitter   ║   │
 *   │ └──────╵──────────────────────────┘  ╚════════════╝   │
 *   └──────────────────────────────────────────────────────┘
 *
 * PLANE 1 — rail + center = ONE FLUSH integrated base. Full-bleed,
 * transparent, FLAT: no notch, no per-pane float shadow, no rounded panel
 * chrome. The rail and the center read as one continuous mood-lit canvas,
 * divided only by a 1px rail line (the hairline token). This replaces the
 * superseded steel "three floating panels" model (#127 v1).
 *
 * PLANE 2 — the Brain (and the consumer's composer) FLOAT inline as raised
 * frosted glass, rendered through the merged `GlassSurface` primitive (#152):
 * a two-layer `backdrop-filter` frost over a `filter: var(--dropFloat)`
 * backing, hex-notch clipped, lifted off the flush base by a 14px gutter. The
 * float is the glass `--dropFloat` shadow, NOT a `box-shadow`.
 *
 * Why FLEX, not GRID (load-bearing). The Brain region width is a controlled
 * prop that animates via `flex-basis` / `width`. A `grid-template-columns:
 * 1fr <animated>px` transition leaves the `1fr` track stuck (the browser
 * cannot interpolate `fr` against `px`), so the elastic center pane freezes
 * mid-animation. Flex with `flex: 0 0 {w}px` on the Brain and
 * `flex: 1 1 0; min-width: 0` on the center sidesteps that entirely. Do NOT
 * reintroduce grid tracks on the resizable axis.
 *
 *   <AppShell
 *     rail={<NavRail … />}              // left slot, fixed width (railWidth)
 *     topbar={<Topbar … />}             // optional top slot (chat-first 3-pane omits it)
 *     brain={brainNode | null}          // right region; null => single-column
 *     brainWidth={panelW}               // controlled px width
 *     brainMinWidth={360} brainMaxWidth={760}
 *     onBrainResize={(w) => setPanelW(w)}
 *     field={<MoodField />}             // optional ambient layer (pointer-events:none)
 *   >
 *     {children /* the center pane / routed <Outlet/> *\/}
 *   </AppShell>
 *
 * "Earns its width" (#103). `brain={null}` renders a single-column frame; the
 * center pane runs a transform-only entrance (a width/translate grow, NEVER
 * opacity-from-0, so html-to-image / PDF export never blanks). Supplying a
 * brain node slides the region in from 0 to its width at 240ms out-soft.
 *
 * Auto-widen. Drive `brainWidth` up (470 / 520 / 580) to focus a drill-in;
 * the width animates and the splitter's `aria-valuenow` follows. The widen
 * policy (grow-never-shrink past the user's manual width, collapse back to
 * 396) lives in the CONSUMER's width state machine - AppShell is controlled
 * and just paints whatever `brainWidth` it is handed.
 *
 * Accessible splitter (W3C WAI-ARIA Window Splitter). The handle on the
 * Brain's LEFT edge is `role="separator"` + `aria-orientation="vertical"` +
 * `aria-valuenow/min/max` + `aria-controls`, focusable, with Arrow-key nudge
 * and Home/End jump-to-min/max plus a pointer drag clamped to
 * [brainMinWidth, brainMaxWidth]. The drag uses minus-delta math (the handle
 * sits on the left edge of a right-docked pane, so dragging left grows it).
 *
 * Tokens. The flush base reads the UNIFIED token names the consumer app
 * defines (`--bg`, `--hairline`, `--rail-w`, `--topbar-h`); each `var()`
 * carries a fallback to an existing lib token (`--background`, `--border`, …)
 * so the frame stays visible in Storybook / playground pre-unified#43. The
 * FLOATING Brain reads only the onyx-hex glass token family through
 * `GlassSurface` (`--glass`, `--glassBd`, `--glassSheen`, `--dropFloat`, …),
 * which already ships in this library's preset + `onyx-hex.css`. No
 * `--surface` fill, `--shadow-card` box-shadow, or steel panel chrome remains.
 * TODO(#43): drop the base fallbacks once unified#43 lands the token ramp.
 *
 * Theme. Reads token names only, so it recolors with zero edits when a
 * `.dark` / `[data-theme="dark"]` block defines the dark values (the 7 glass
 * token values flip there). The action accent is `--forest` by name; its
 * VALUE (the navy-vs-blue #103/#43 accent conflict) is owned by unified#43.
 */

/* Unified base token names with lib-token fallbacks. Only the FLUSH base
 * (canvas fill + rail line) needs these; the floating Brain's depth comes
 * entirely from GlassSurface's onyx-hex glass tokens. TODO(#43): the fallbacks
 * become dead once unified#43 ships these names into the lib token sheet. */
const TOKEN = {
  bg: "var(--bg, hsl(var(--background)))",
  hairline: "var(--hairline, hsl(var(--border)))",
  railW: "var(--rail-w, 72px)",
  topbarH: "var(--topbar-h, 52px)",
} as const;

/** Default Brain width when a session first "earns" the region (#103). */
export const DEFAULT_BRAIN_WIDTH = 396;
/** Default clamp floor for the splitter / drag. */
export const DEFAULT_BRAIN_MIN_WIDTH = 360;
/** Default clamp ceiling for the splitter / drag. */
export const DEFAULT_BRAIN_MAX_WIDTH = 760;
/** Brain width transition: base (240ms), out-soft. Matches the motion contract. */
const BRAIN_TRANSITION = "width 240ms cubic-bezier(0.22, 1, 0.36, 1), flex-basis 240ms cubic-bezier(0.22, 1, 0.36, 1)";
/** Gutter (px) the floating Brain glass is inset from the viewport edges. */
const BRAIN_GUTTER = 14;

const appShellVariants = cva(
  // Fixed, full-viewport, clipped. Background = the unified --bg token.
  "fixed inset-0 overflow-hidden",
);

export interface AppShellProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof appShellVariants> {
  /**
   * Left slot - the fixed-width icon column (NavRail lives here; AppShell only
   * reserves the slot). Omit to drop the rail entirely.
   */
  rail?: React.ReactNode;
  /**
   * Optional top slot. Present for the topbar + rail listing surfaces
   * (Home / Studios); omitted for the chat-first 3-pane. Spans the full row
   * above the rail + center + Brain.
   */
  topbar?: React.ReactNode;
  /**
   * Right region - the controlled-width Brain pane (BrainPanel router /
   * SessionSidebar live here). `null` (or omitted) renders a single-column
   * frame; the region "earns its width" once a node is supplied.
   */
  brain?: React.ReactNode;
  /** Controlled Brain width in px. Default {@link DEFAULT_BRAIN_WIDTH} (396). */
  brainWidth?: number;
  /** Drag / keyboard clamp floor. Default {@link DEFAULT_BRAIN_MIN_WIDTH} (360). */
  brainMinWidth?: number;
  /** Drag / keyboard clamp ceiling. Default {@link DEFAULT_BRAIN_MAX_WIDTH} (760). */
  brainMaxWidth?: number;
  /**
   * Fired by the splitter (pointer drag + Arrow / Home / End keys) with the
   * next clamped width. Omit to make the Brain non-resizable (the splitter is
   * not rendered).
   */
  onBrainResize?: (width: number) => void;
  /**
   * Px the Arrow keys nudge the Brain width per press. Default 24. Shift+Arrow
   * uses 4x this for a coarse jump.
   */
  brainResizeStep?: number;
  /**
   * Optional ambient FIELD layer (mood-lighting). Rendered absolute, behind the
   * panes, `pointer-events:none` so it never eats clicks. AppShell owns the
   * slot; the ambient art is the consumer's (MoodLighting / BorderGlow).
   */
  field?: React.ReactNode;
  /**
   * Rail slot width. Accepts a px number or any CSS length string. Default
   * `var(--rail-w)` so the consumer's layout token drives it.
   */
  railWidth?: number | string;
  /** Accessible label for the resize splitter. Default "Resize Brain panel". */
  splitterLabel?: string;
  /** The center pane content (the routed view / chat). */
  children?: React.ReactNode;
}

export const AppShell = React.forwardRef<HTMLDivElement, AppShellProps>(
  (
    {
      className,
      style,
      rail,
      topbar,
      brain,
      brainWidth = DEFAULT_BRAIN_WIDTH,
      brainMinWidth = DEFAULT_BRAIN_MIN_WIDTH,
      brainMaxWidth = DEFAULT_BRAIN_MAX_WIDTH,
      onBrainResize,
      brainResizeStep = 24,
      field,
      railWidth,
      splitterLabel = "Resize Brain panel",
      children,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion() ?? false;
    const hasBrain = brain != null;
    const resizable = hasBrain && typeof onBrainResize === "function";
    // Stable id so the splitter's `aria-controls` points at the Brain region
    // it resizes (WAI-ARIA Window Splitter pattern).
    const brainId = React.useId();

    // Single-column "earns its width" entrance (#103). Transform-only: the
    // center starts nudged + very slightly scaled and settles to identity on
    // mount, so a fresh single-column session animates in WITHOUT an
    // opacity-from-0 blank frame (html-to-image / PDF export stays correct).
    // Only runs while there is no Brain - once the Brain mounts the row owns
    // the geometry. Reduced-motion paints the final state immediately.
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }, []);
    const earnEntrance = !hasBrain && !reduced;
    // The center is part of the FLUSH base now: flat + transparent, no panel
    // fill/border/shadow. Only the transform-only entrance rides on it (never
    // opacity-from-0, so html-to-image / PDF export never blanks).
    const centerStyle: React.CSSProperties | undefined = earnEntrance
      ? {
          transform: mounted ? "none" : "translateX(8px) scale(0.992)",
          transformOrigin: "center right",
          transition: "transform 240ms cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "transform",
        }
      : undefined;

    // Clamp the incoming width so aria-valuenow + the inline width never
    // exceed the advertised range even if the consumer over-drives it.
    const width = Math.max(brainMinWidth, Math.min(brainMaxWidth, brainWidth));

    // Pointer-drag state. `dragging` suppresses the width transition so the
    // pane tracks the cursor 1:1 (transitions are for programmatic widen /
    // collapse, not the live drag).
    const [dragging, setDragging] = React.useState(false);
    const dragRef = React.useRef<{ startX: number; startW: number } | null>(null);

    const clamp = React.useCallback(
      (w: number) => Math.max(brainMinWidth, Math.min(brainMaxWidth, w)),
      [brainMinWidth, brainMaxWidth],
    );

    const handlePointerDown = React.useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!resizable) return;
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        dragRef.current = { startX: e.clientX, startW: width };
        setDragging(true);
      },
      [resizable, width],
    );

    const handlePointerMove = React.useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        const start = dragRef.current;
        if (!start || !onBrainResize) return;
        // Minus-delta: the handle is on the LEFT edge of a right-docked pane,
        // so dragging left (clientX decreases) GROWS the Brain.
        const next = clamp(start.startW - (e.clientX - start.startX));
        if (next !== width) onBrainResize(next);
      },
      [onBrainResize, clamp, width],
    );

    const endDrag = React.useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragRef.current) return;
        (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
        dragRef.current = null;
        setDragging(false);
      },
      [],
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!onBrainResize) return;
        const coarse = e.shiftKey ? brainResizeStep * 4 : brainResizeStep;
        let next: number | null = null;
        switch (e.key) {
          // ArrowLeft grows (matches the left-edge drag direction), ArrowRight shrinks.
          case "ArrowLeft":
            next = clamp(width + coarse);
            break;
          case "ArrowRight":
            next = clamp(width - coarse);
            break;
          case "Home":
            next = brainMinWidth;
            break;
          case "End":
            next = brainMaxWidth;
            break;
          default:
            return;
        }
        e.preventDefault();
        if (next != null && next !== width) onBrainResize(next);
      },
      [onBrainResize, brainResizeStep, clamp, width, brainMinWidth, brainMaxWidth],
    );

    // Rail is part of the FLUSH base: a transparent slot divided from the
    // center by a single 1px rail line (the hairline token) on its right edge.
    // No panel fill, border-box, notch, or float shadow.
    const railStyle: React.CSSProperties = {
      flex: `0 0 ${railWidth != null ? toLen(railWidth) : TOKEN.railW}`,
      width: railWidth != null ? toLen(railWidth) : TOKEN.railW,
      borderRight: `1px solid ${TOKEN.hairline}`,
    };

    // Brain region geometry (the FLEX CHILD that holds the floating glass).
    // Its outer width is the controlled Brain width + the gutter the glass
    // floats inside, via inline style + `flex 0 0 Wpx` (NOT a grid track) so
    // the elastic center can shrink. `aria-valuenow` reports the inner glass
    // `width` (what the consumer drives), so the gutter is added only to the
    // flex basis here. Depth is the GlassSurface `--dropFloat` shadow, never a
    // box-shadow. NOTE: no `contain: paint` here — the float drop-shadow bleeds
    // past the glass box into the canvas, and `contain: paint` would clip it.
    const brainStyle: React.CSSProperties = {
      width: width + BRAIN_GUTTER,
      flex: `0 0 ${width + BRAIN_GUTTER}px`,
      transition: dragging || reduced ? "none" : BRAIN_TRANSITION,
    };

    return (
      <div
        ref={ref}
        className={cn(appShellVariants(), className)}
        style={{ background: TOKEN.bg, ...style }}
        {...props}
      >
        {/* Ambient FIELD layer - behind the panes, never eats clicks. */}
        {field != null ? (
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
            {field}
          </div>
        ) : null}

        {/* The flex column: optional topbar over the rail/center/Brain row. */}
        <div className="absolute inset-0 z-10 flex flex-col">
          {topbar != null ? (
            <div
              className="flex-none"
              style={{ height: TOKEN.topbarH }}
            >
              {topbar}
            </div>
          ) : null}

          {/* THE FLEX ROW - the two-plane base. Rail + center are flush
              (no gap, no padding); the Brain floats with its own gutter. NOT
              a grid on the resizable axis. */}
          <div className="flex min-h-0 flex-1">
            {/* Rail - flush base slot, divided from the center by a 1px rail
                line (railStyle.borderRight). Transparent + flat: no panel
                fill, notch, or float shadow. */}
            {rail != null ? (
              <div className="relative overflow-hidden" style={railStyle}>
                {rail}
              </div>
            ) : null}

            {/* Center - flush base, FLAT + transparent (no panel chrome).
                Elastic, min-w-0 so it can shrink below intrinsic width when the
                Brain widens (without this the resize jams). When the Brain is
                absent it runs a transform-only "earns its width" entrance so a
                fresh single-column session animates in without an opacity
                blank-frame. */}
            <div
              className="relative flex min-w-0 flex-1 flex-col overflow-hidden"
              style={centerStyle}
            >
              {children}
            </div>

            {/* Brain region - the FLOATING plane. The flex child owns the
                controlled width (+ gutter) and the splitter; the frosted glass
                surface floats inside it via GlassSurface. */}
            {hasBrain ? (
              <div id={brainId} className="relative" style={brainStyle}>
                {/* Accessible splitter - the Brain's LEFT edge (the seam
                    against the flush center). A base-plane control, so it sits
                    on the flex child, not inside the glass. */}
                {resizable ? (
                  <div
                    role="separator"
                    tabIndex={0}
                    aria-orientation="vertical"
                    aria-label={splitterLabel}
                    aria-controls={brainId}
                    aria-valuenow={Math.round(width)}
                    aria-valuemin={brainMinWidth}
                    aria-valuemax={brainMaxWidth}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyDown={handleKeyDown}
                    className={cn(
                      "absolute left-[-3px] top-0 bottom-0 z-20 w-[9px] cursor-ew-resize touch-none",
                      "focus:outline-none",
                      // Focus-visible keyline on the edge it controls.
                      "focus-visible:before:absolute focus-visible:before:left-[3px] focus-visible:before:top-0 focus-visible:before:bottom-0 focus-visible:before:w-[2px]",
                      "focus-visible:before:bg-[var(--forest,hsl(var(--accent)))] focus-visible:before:content-['']",
                    )}
                  />
                ) : null}

                {/* The floating frosted Brain - merged GlassSurface (#152):
                    two-layer backdrop-filter frost + --dropFloat backing, hex
                    notch, inset --glassBd rim. Floats with the gutter on top /
                    bottom / right; the left edge meets the splitter seam.
                    Sizing/positioning ride wrapperClassName so the inner glass
                    layers keep the filter chain intact. */}
                <GlassSurface
                  variant="panel"
                  nx="l"
                  wrapperClassName="overflow-hidden"
                  className="flex h-full flex-col overflow-hidden"
                  style={{
                    width,
                    marginTop: BRAIN_GUTTER,
                    marginBottom: BRAIN_GUTTER,
                    marginRight: BRAIN_GUTTER,
                    height: `calc(100% - ${BRAIN_GUTTER * 2}px)`,
                  }}
                >
                  {brain}
                </GlassSurface>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);
AppShell.displayName = "AppShell";

/** Coerce a px-number or CSS length into a CSS length string. */
function toLen(v: number | string): string {
  return typeof v === "number" ? `${v}px` : v;
}
